import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Backdrop,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db } from "../api/firebase";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const functions = getFunctions();
const sendMail = httpsCallable(functions, "sendBankTransferConfirmation");

const StripeCheckoutButton2025 = ({
  userId,
  priceId,
  price,
  title,
  description,
  paymentPhase,
  hasPayment,
  name,
  formattedPhone,
  displayStart,
  displayEnd,
  paymentPhaseId,
  onBankTransferRequestComplete,
  isEarlyBird,
  couponCode = null,
  stripePromotionCodeId = null,
  couponId = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    const stripe = await stripePromise;
    const functions = getFunctions();
    const createCheckoutSession = httpsCallable(
      functions,
      "createCheckoutSession"
    );

    const user = auth.currentUser;
    if (!user?.email) {
      alert("ログインが必要です");
      return;
    }

    console.log("user.email", user.email);
    try {
      const payload = {
        priceId,
        successUrl: "https://mastermindmembers.net/payment/success",
        cancelUrl: "https://mastermindmembers.net/payment/cancel",
        userId,
        paymentPhase,
        email: user.email,
      };
      // クーポン適用時はStripe Promotion Code IDを渡す
      if (stripePromotionCodeId) {
        payload.stripePromotionCodeId = stripePromotionCodeId;
        payload.couponCode = couponCode;
        payload.couponId = couponId;
      }
      const { data } = await createCheckoutSession(payload);

      await stripe.redirectToCheckout({ sessionId: data.id });
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setLoading(false);
    }
  };

  const formatAmount = (value) => {
    if (value == null) return "";
    if (typeof value === "number") return `${value.toLocaleString()}円`;
    return String(value); // すでに「28,000円」などの文字列ならそのまま
  };

  const handleBankTransferApplication = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("ログインが必要です。");
        return;
      }
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        alert("ユーザーデータの取得に失敗しました。");
        return;
      }
      const userData = userDocSnap.data();

      // 🔽 ここから追加
      // 電話番号の下4桁を取り出す（formattedPhone優先 → 登録電話）
      const rawPhone =
        formattedPhone || userData.registrationPhone || userData.phone || "";

      const digits = String(rawPhone).replace(/\D/g, ""); // 数字以外を除去
      const last4 = digits.slice(-4);

      // ひらがな → カタカナ
      const toKatakana = (str = "") => {
        return str.replace(/[\u3041-\u3096]/g, (ch) =>
          String.fromCharCode(ch.charCodeAt(0) + 0x60)
        );
      };
      // ひらがなを含むフリガナをすべてカタカナに統一
      const registrationFuriganaKatakana = userData.registrationFurigana
        ? toKatakana(userData.registrationFurigana)
        : "";

      // 表示用の名前（フリガナ優先 → 登録名 → props.name → displayName）
      const displayName =
        registrationFuriganaKatakana ||
        userData.registrationName ||
        name ||
        userData.displayName ||
        "";
      // 振込人名（"1234 マスターマインド タロウ" みたいな形）
      const transferDisplayName =
        last4 && displayName ? `${last4} ${displayName}` : "";
      // 🔼 ここまで追加

      const bankTransferData = {
        userId: user.uid,
        paymentPhaseId,
        requestedAt: serverTimestamp(),
        amount: price,
        formattedPhone,
        email: user.email,
        prefecture: userData.prefecture || "",
        registrationPhone: userData.registrationPhone || "",
        registrationName: userData.registrationName || "",
        registrationFurigana: userData.registrationFurigana || "",
      };
      if (couponCode) {
        bankTransferData.couponCode = couponCode;
        bankTransferData.couponId = couponId;
      }
      await addDoc(collection(db, "bankTransferRequests2026"), bankTransferData);

      // 🔽 親のリフレッシュ関数を呼び出す
      if (onBankTransferRequestComplete) {
        await onBankTransferRequestComplete();
      }

      try {
        await sendMail({
          email: userData.email,
          subject: "【重要】銀行振込のご案内（マスターマインドメンバーズ）",
          text: `
         お世話になります。
マスターマインドメンバーズ事務局です。
この度のご縁に感謝申し上げます！

参加費のお支払い方法は、以下の通りです。

【銀行振込】
振込依頼人名に、お申込み時の電話番号の下4桁を入れてお振込みください。
（例：「2134　マスター　タロウ」）

金融機関：GMOあおぞらネット銀行
支店名：法人第二営業部
口座種別：普通
口座番号：1007963
名義：ファミュニティリンク株式会社イベント
入金額: ${formatAmount(price)}

お振込依頼人名：${transferDisplayName || "（申込時の電話番号下4桁＋お名前）"}

※恐れ入りますが、振込手数料のご負担をお願いいたします。
※本メール到着後、1週間以内のお振込み期限とさせていただきます。

ご案内は以上となります。
ご一緒できますこと、楽しみにお待ちしております！


マスターマインドメンバーズ事務局
            `,
        });
      } catch (err) {
        console.error("メール送信失敗:", err);
        // メールが失敗しても先に進める（通知だけ）
      }

      setConfirmDialogOpen(false);
      alert("振込申込を受け付けました。");
    } catch (err) {
      console.error("申込処理エラー:", err);
      alert("申込に失敗しました。時間をおいて再度お試しください。");
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={4}
      sx={{ mt: 5 }}
    >
      <Card
        sx={{
          boxShadow: 3,
          padding: "16px",
          maxWidth: 400,
          backgroundColor: hasPayment ? "rgba(0, 0, 0, 0.1)" : "white",
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", marginBottom: "8px" }}
          >
            {title}
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: "8px" }}>
            {description}
          </Typography>

          {/* 対象期間表示 */}
          {displayStart && (
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              {isEarlyBird ? "早期受付期間" : "対象期間"}: {displayStart} 〜{" "}
              {displayEnd}
            </Typography>
          )}

          <Typography
            variant="h6"
            sx={{
              color: hasPayment ? "text.secondary" : "primary.main",
              fontWeight: "bold",
            }}
          >
            {price || "価格未設定"}
          </Typography>

          {hasPayment && (
            <Typography variant="body2" sx={{ color: "red", marginTop: 2 }}>
              既に支払い済みです。
            </Typography>
          )}

          {!hasPayment && (
            <>
              <Divider sx={{ marginY: 2 }} />

              <Button
                variant="outlined"
                onClick={() => setConfirmDialogOpen(true)}
                sx={{ mb: 2 }}
              >
                銀行振込で支払う
              </Button>

              <Divider sx={{ marginY: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  or
                </Typography>
              </Divider>

              <Button
                variant="contained"
                color="primary"
                onClick={handleCheckout}
                disabled={loading}
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  padding: "10px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                クレジットカードで支払う
              </Button>

              {/* ★ ここ */}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "error.main",
                  fontWeight: 700,
                  lineHeight: 1.6,
                }}
              >
                ※ ご入金後のキャンセルは承っておりません。
                ご理解の上、決済のお手続きをお願い申し上げます。
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* 最終確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        fullWidth
      >
        <DialogTitle>重要なご案内</DialogTitle>
        <DialogContent>
          {couponCode && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(229,57,53,0.08)",
                border: "1px solid rgba(229,57,53,0.3)",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#e53935" }}
              >
                クーポン適用中: {couponCode}
              </Typography>
              <Typography variant="body2" sx={{ color: "#e53935" }}>
                お振込金額: {price}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            銀行振込は、<strong>お申込日より1週間以内</strong>
            にお願いいたします。
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            1週間を過ぎると、<strong>お申込みが破棄</strong>されます。
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            お申込みが破棄されると、<strong>新たにお申込み</strong>
            いただく必要があります。
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            その際、<strong>前回のお申込み内容は復元できません</strong>
            。サービス内容や価格が異なる場合もありますのでご注意ください。
          </Typography>

          <Box display="flex" alignItems="center" mt={3}>
            <input
              type="checkbox"
              id="agree"
              checked={agreeChecked}
              onChange={(e) => setAgreeChecked(e.target.checked)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor="agree">上記の内容を把握しました。</label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={!agreeChecked}
            onClick={handleBankTransferApplication}
          >
            申込み
          </Button>
        </DialogActions>
      </Dialog>

      {/* stripe呼び出し */}
      <Backdrop
        open={loading}
        sx={{
          zIndex: 1300,
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            支払いページ準備中...
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default StripeCheckoutButton2025;
